import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import io from "socket.io-client";
import Cookies from "universal-cookie";
import { scanUser } from "../pages/users/partials/log";
import { expiredUser } from "../pages/users/partials/logout";

const cookies = new Cookies();

const getToken = () => {
  const Retoken: string = cookies.get("token");
  return Retoken;
};

const withRetries = new RetryLink({
  delay: {
    max: 30 * 1000,
  },
  attempts: (count, operation, error) => {
    if (error && error.toString().indexOf("aborted") !== -1) {
      console.log(`error ${operation.operationName} ${error}`);
      count = 0;
      return false;
    } else {
      if (count >= 3) {
        console.log(
          `Retrying operation ${operation.operationName} (Attempt ${count})`,
        );
        intrigueToken();
        if (count >= 5) return false;
      }
    }

    return !!error;
  },
});

const link = createHttpLink({ uri: process.env.REACT_APP_GRAPHQL_URL });

let websocketRetryAttempt = 0;

const wsClient = createClient({
  url: "wss://apollo-subscription.forstok.com/graphql",
  lazy: true,
  retryAttempts: Infinity,
  connectionParams: () => {
    const token = getToken();
    intrigueToken();

    return {
      authorization: token ? `Bearer ${token}` : "",
    };
  },
  shouldRetry: () => {
    websocketRetryAttempt += 1;
    console.log(`websocket retry attempt ${websocketRetryAttempt}`);
    return true;
  },
  on: {
    connected: (socket) => {
      websocketRetryAttempt = 0;
      const activeSocket = socket as WebSocket;
      console.log("websocket connected!!", {
        protocol: activeSocket.protocol || null,
      });
    },
    closed: (event) => {
      const closeEvent = event as CloseEvent;
      console.log("websocket closed!!", {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        willRetry: true,
      });
    },
    error: (error) => {
      console.log("websocket error!!", error);
    },
  },
});
const wsLink = new GraphQLWsLink(wsClient);

const intrigueToken = () => {
  const token = getToken();
  if (!token) {
    const callbackToken = () => {
      expiredUser();
    };
    scanUser()
      .then((result) => {
        if (!result) {
          callbackToken();
        }
      })
      .catch((error) => {
        console.error("Error Checking Login", error);
        callbackToken();
      });
  }
};

const forstokHttpLink = ApolloLink.from([withRetries, link]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  forstokHttpLink,
);

const forstokAuthLink = setContext((_, { headers }) => {
  const token = getToken();
  intrigueToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        orders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        order(_, { args, toReference }) {
          return toReference({
            __typename: "Order",
            id: args?.id || "",
          });
        },
        qtyInventories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockInbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockOutbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
        stockHistoryList: {
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    Warehouses: {
      keyFields: ["id"],
    },
    Accounts: {
      keyFields: ["id", ["name"]],
    },
    ProductWarehouse: {
      keyFields: false,
      merge: true,
    },
    WarehouseInventoryPromotion: {
      keyFields: false,
    },
    WarehouseInventoryReservedOrder: {
      keyFields: false,
    },
  },
  addTypename: true,
});

export const client = new ApolloClient({
  link: forstokAuthLink.concat(splitLink),
  cache,
  queryDeduplication: false,
});

export const socket = io("wss://socket.forstok.com");
